import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PageTableLayout from "../../../../components/PageTableLayout";
import apiService from "../../../../services/apiService";
import { FaFilter, FaTimes, FaEye, FaPencilAlt, FaPlus } from "react-icons/fa";
import { Button } from "../../../../components/ui";

const breadcrumb = (
  <span>
    Home &nbsp;/&nbsp; <span className="text-gray-700">Courses</span>
  </span>
);

const Course = () => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({
    name: "",
    eduStandard: "",
    status: "",
  });
  // No need for courses state as this is the courses list
  const [eduStandards, setEduStandards] = useState([]);
  const [limit, setLimit] = useState(10);
  const [isFiltered, setIsFiltered] = useState(false);

  const fetchData = useCallback(
    async (page, filterParams = {}, itemsPerPage = limit) => {
      try {
        setLoading(true);

        // Build query parameters
        const params = new URLSearchParams({
          page,
          limit: itemsPerPage,
          totalCount: true,
        });

        // Add any filter parameters
        Object.entries(filterParams).forEach(([key, value]) => {
          if (value) {
            params.append(key, value);
          }
        });

        const response = await apiService.request(
          `/i/ims/course?${params.toString()}`,
        );

        // Handle the response data format
        if (response && response.data && Array.isArray(response.data)) {
          setData(response.data);
          setTotalRecords(response.totalCount || 0);
        } else if (Array.isArray(response)) {
          setData(response);
          // If totalCount is not available, use the array length
          setTotalRecords(response.length);
        } else {
          console.error("Unexpected response format:", response);
          setData([]);
          setTotalRecords(0);
        }

        setCurrentPage(page);

        // Check if any filters are applied
        setIsFiltered(
          Object.values(filterParams).some((value) => value !== ""),
        );
      } catch (error) {
        console.error("Error fetching courses:", error);
        setData([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    },
    [limit],
  );

  // No need to fetch courses as this is the courses list

  // Fetch education standards for filter dropdown
  const fetchEduStandards = useCallback(async () => {
    try {
      const eduStandardData = await apiService.request(
        "/i/ims/edustandard?limit=all&status=true&select=name",
      );

      if (Array.isArray(eduStandardData)) {
        setEduStandards(eduStandardData);
      } else {
        console.error(
          "Education standard data is not an array:",
          eduStandardData,
        );
        setEduStandards([]);
      }
    } catch (err) {
      console.error("Error fetching education standards:", err);
      setEduStandards([]);
    }
  }, []);

  useEffect(() => {
    fetchData(1);
    fetchEduStandards();
  }, [fetchData, fetchEduStandards]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle filter submit
  const handleFilterSubmit = () => {
    fetchData(1, filters);
  };

  // Handle filter reset
  const handleFilterReset = () => {
    const resetFilters = {
      name: "",
      eduStandard: "",
      status: "",
    };
    setFilters(resetFilters);
    fetchData(1, resetFilters);
  };

  // Handle page change
  const handlePageChange = (page) => {
    fetchData(page, filters);
  };

  // Handle limit change
  const handleLimitChange = (value) => {
    // Handle both direct value and event object
    const newLimit = Number(
      typeof value === "object" ? value.target.value : value,
    );
    setLimit(newLimit);
    // Reset to first page when changing limit
    fetchData(1, filters, newLimit);
  };

  const columns = [
    {
      key: "name",
      title: "Name",
      filterComponent: (
        <input
          className="w-full border px-2 py-1 rounded text-xs"
          placeholder="Filter by name"
          value={filters.name}
          onChange={(e) => handleFilterChange("name", e.target.value)}
        />
      ),
    },
    {
      key: "uri",
      title: "URI",
      filterComponent: (
        <input
          className="w-full border px-2 py-1 rounded text-xs"
          placeholder="Filter by URI"
          value={filters.uri}
          onChange={(e) => handleFilterChange("uri", e.target.value)}
        />
      ),
    },
    {
      key: "order",
      title: "Order",
      filterComponent: null, // No filter for order column
    },
    {
      key: "status",
      title: "Status",
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${row.status ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
        >
          {row.status ? "Active" : "Inactive"}
        </span>
      ),
      filterComponent: (
        <select
          className="w-full border px-2 py-1 rounded text-xs"
          value={filters.status}
          onChange={(e) => handleFilterChange("status", e.target.value)}
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      render: (row) => (
        <div className="flex space-x-2">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/course-programs/courses/view/${row.id}`);
            }}
            variant="outline"
            size="xs"
            title="View"
            className="text-blue-600 border-blue-600 hover:bg-blue-50"
          >
            <FaEye className="h-4 w-4" />
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/course-programs/courses/edit/${row.id}`);
            }}
            variant="outline"
            size="xs"
            title="Edit"
            className="text-amber-600 border-amber-600 hover:bg-amber-50"
          >
            <FaPencilAlt className="h-4 w-4" />
          </Button>
        </div>
      ),
      filterComponent: (
        <div className="flex space-x-1">
          <Button
            variant="outline"
            size="xs"
            className="text-blue-600 border-blue-600 hover:bg-blue-50"
            onClick={handleFilterSubmit}
            disabled={
              !filters.name &&
              !filters.course &&
              !filters.eduStandard &&
              !["true", "false"].includes(filters.status)
            }
          >
            <FaFilter className="h-3 w-3 mr-1" />
            Filter
          </Button>
          <Button
            variant="outline"
            size="xs"
            className="text-red-600 border-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleFilterReset}
            disabled={!isFiltered}
          >
            <FaTimes className="h-3 w-3 mr-1" />
            Reset
          </Button>
        </div>
      ),
    },
  ];

  const handleView = (id) => {
    navigate(`/course-programs/courses/view/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/course-programs/courses/edit/${id}`);
  };

  const handleAddNew = () => {
    navigate("/course-programs/courses/add");
  };

  return (
    <PageTableLayout
      title="Course Programs"
      breadcrumb={breadcrumb}
      columns={columns}
      data={data || []}
      loading={loading}
      pagination={{
        currentPage,
        totalRecords,
        totalPages: Math.ceil(totalRecords / limit),
        limit,
        onLimitChange: handleLimitChange,
      }}
      onPageChange={handlePageChange}
      showFiltersInTable={true}
    />
  );
};

export default Course;
